import { defineRecipe } from '@chakra-ui/react';

/*
 * Design system source: .claude/skills/monetizenow-design/preview/inputs.html
 *
 * Default size: sm → 30px tall, 12-13px font, 4px radius, 1px border-default.
 * Focus: border → indigo + 3px indigo halo (shadows.focus).
 * Unsaved: 3px orange left border (legacy pattern preserved).
 * Error: 3px red left border.
 * Readonly: transparent with hover nudge to subtle bg.
 *
 * v3 note: Chakra's `Input` is a single flat element in v3 (no more
 * field/addon slot split — `InputAddon` isn't used anywhere in this app),
 * so this is a plain `defineRecipe`, not a slot recipe. `Textarea.ts`
 * reuses `sharedFieldBase`/`disabledFieldStyle` from here directly rather
 * than reaching into this recipe's compiled runtime shape (defineRecipe's
 * result is a callable variant function, not a plain baseStyle/variants
 * object like v2's defineStyleConfig).
 */

export const sizes = {
  xl: { fontSize: 'xl', px: 3, h: '44px', borderRadius: 'sm' },
  lg: { fontSize: 'lg', px: 3, h: '36px', borderRadius: 'sm' },
  md: { fontSize: 'sm', px: 3, h: '32px', borderRadius: 'sm' },
  sm: { fontSize: 'sm', px: '10px', h: '30px', borderRadius: 'sm' },
  xs: { fontSize: 'sm', px: 2, h: '26px', borderRadius: 'sm' },
};

/**
 * Shared disabled treatment for input-like fields.
 *
 * Exported because Chakra merges style props over recipe styles shallowly:
 * a call site that passes its own `_disabled` replaces this entire block
 * rather than merging into it, silently dropping the border and background
 * tokens. A call site that needs to change one key (e.g. keep a meaningful
 * placeholder legible) must spread this instead of hand-rolling a partial
 * `_disabled`.
 */
export const disabledFieldStyle = {
  cursor: 'not-allowed',
  color: 'fg.muted',
  background: 'bg.muted',
  borderColor: 'border.subtle',
  opacity: 1,
  _placeholder: { color: 'transparent' },
};

export const sharedFieldBase = {
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  bg: 'bg.surface',
  color: 'fg.primary',
  fontWeight: 'normal',
  transition: 'border-color 120ms, box-shadow 120ms, background-color 120ms',
  _disabled: disabledFieldStyle,
  _invalid: {
    borderColor: 'tRed.base',
    borderWidth: '1px',
    color: 'tRed.base',
  },
  // Focus signal is the halo only — the default 1px border stays its
  // resting `border.default` gray. Doubling up (indigo border + halo) read
  // as too heavy in dense forms. Search variant uses an inset halo for
  // popover/dropdown contexts where an outer halo would clip.
  //
  // `!important` on box-shadow because some consumers (e.g. DatePicker,
  // MCustomSelect) wrap the Input in a Chakra Popover and the popover's
  // own focus management can override box-shadow. Using !important here
  // wins against any consumer-level inline override too.
  _focusVisible: {
    zIndex: 1,
    boxShadow: 'var(--chakra-shadows-focus) !important',
    bg: 'bg.surface',
    color: 'fg.primary',
  },
  _focus: {
    zIndex: 1,
    boxShadow: 'var(--chakra-shadows-focus) !important',
    bg: 'bg.surface',
    color: 'fg.primary',
    // ReadOnly inputs (e.g. trigger fields like the date picker) still need
    // a visible focus ring for keyboard nav — only the dedicated `readonly`
    // *variant* (used for chrome-less display-only fields) suppresses it.
  },
  _placeholder: {
    color: 'fg.subtle',
    fontWeight: 'normal',
  },
  _readOnly: {
    cursor: 'text',
  },
};

export const readonlyFieldStyle = {
  ...sharedFieldBase,
  bg: 'transparent',
  borderColor: 'transparent',
  _readOnly: {
    // Longhands, not `border: none`: the shorthand also resets the color to
    // `currentColor`, so a field switching to an editable variant (account
    // details' Edit) faded its border from the near-black text color to gray.
    borderStyle: 'none !important',
    borderColor: 'transparent !important',
    paddingLeft: 0,
    opacity: 1,
    resize: 'none',
    color: 'fg.primary',
    bg: 'transparent !important',
    boxShadow: 'none !important',
    userSelect: 'all',
    _placeholder: { color: 'transparent' },
  },
};

export const unsavedFieldStyle = {
  ...sharedFieldBase,
  borderLeftWidth: '3px',
  borderLeftColor: 'tOrange.base',
  // Logical `ps`, not physical `pl` — InputGroup injects `paddingStart` on
  // the input to reserve room for an InputLeftElement, and a physical
  // `padding-left` here would serialize *after* that logical declaration and
  // win the cascade, dropping the reserved space and letting the left icon
  // sit on top of the value.
  ps: '8px',
  _hover: { borderLeftColor: 'tOrange.base' },
  _focus: {
    ...sharedFieldBase._focus,
    borderLeftColor: 'tOrange.base !important',
    borderLeftWidth: '3px',
  },
};

export const errorFieldStyle = {
  ...sharedFieldBase,
  borderLeftWidth: '3px',
  borderLeftColor: 'tRed.base',
  // Logical `ps` — see the note on `unsavedFieldStyle`.
  ps: '8px',
  color: 'tRed.base',
  _hover: { borderLeftColor: 'tRed.base' },
  _focus: {
    ...sharedFieldBase._focus,
    borderLeftColor: 'tRed.base !important',
    borderLeftWidth: '3px',
    boxShadow: 'focusInsetDanger',
  },
};

export const inputTheme = defineRecipe({
  base: sharedFieldBase,
  variants: {
    variant: {
      search: {
        ...sharedFieldBase,
        bg: 'bg.muted',
        borderColor: 'transparent',
        // Search inputs frequently sit inside popovers/dropdowns, where an
        // outer halo would clip the parent edge. Use the inset focus ring
        // for these.
        _focus: {
          ...sharedFieldBase._focus,
          bg: 'bg.surface',
          boxShadow: 'focusInset',
        },
        _focusVisible: {
          ...sharedFieldBase._focusVisible,
          bg: 'bg.surface',
          boxShadow: 'focusInset',
        },
      },
      quantity: {
        ...sharedFieldBase,
        fontWeight: 'bold',
        _readOnly: {
          boxShadow: 'none !important',
          userSelect: 'all',
        },
      },
      readonly: readonlyFieldStyle,
      unsaved: unsavedFieldStyle,
      error: errorFieldStyle,
      // `outline` — defined last so it cleanly wins over Chakra's own
      // built-in `outline` variant (also named `outline`) when this config
      // merges with defaultConfig. Chakra's default injects a blue focus
      // ring; ours pins borderColor at resting and uses our 3px indigo
      // halo from `shadows.focus`.
      outline: {
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'border.default',
        bg: 'bg.surface',
        color: 'fg.primary',
        fontWeight: 'normal',
        transition:
          'border-color 120ms, box-shadow 120ms, background-color 120ms',
        // The built-in `outline` variant's `focusVisibleRing: 'inside'`
        // survives the merge and paints a 1px near-black (`gray.focusRing`)
        // outline + border on `:focus-visible`, on top of our halo. Also
        // clears it for NumberInput/NativeSelect, which derive from these.
        focusVisibleRing: 'none',
        _hover: {
          borderColor: 'border.default',
        },
        _disabled: disabledFieldStyle,
        _invalid: {
          borderColor: 'tRed.base',
          borderWidth: '1px',
          color: 'tRed.base',
        },
        _focusVisible: {
          zIndex: 1,
          borderColor: 'border.default',
          boxShadow: 'var(--chakra-shadows-focus) !important',
          bg: 'bg.surface',
          color: 'fg.primary',
        },
        _focus: {
          zIndex: 1,
          borderColor: 'border.default',
          boxShadow: 'var(--chakra-shadows-focus) !important',
          bg: 'bg.surface',
          color: 'fg.primary',
        },
        _readOnly: {
          cursor: 'default',
          boxShadow: 'unset',
          _focus: {
            boxShadow: 'var(--chakra-shadows-focus) !important',
          },
          _focusVisible: {
            boxShadow: 'var(--chakra-shadows-focus) !important',
          },
        },
        _placeholder: {
          color: 'fg.subtle',
          fontWeight: 'normal',
        },
      },
    },
    size: sizes,
  },
  defaultVariants: {
    size: 'sm',
    variant: 'outline',
  },
});

export default inputTheme;
